import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, Modal, StyleSheet } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface CancellationPolicyEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentPolicy: string;
    onSave: (newPolicy: string) => void;
    sessionTitle?: string;
}

const CancellationPolicyEditModal: React.FC<CancellationPolicyEditModalProps> = ({
    visible,
    onClose,
    currentPolicy,
    onSave,
    sessionTitle = "Edit Cancellation Policy"
}) => {
    const [selectedPolicy, setSelectedPolicy] = useState(currentPolicy || '7-day');

    // Policy options matching CancellationPicker
    const policyOptions = [
        { label: 'Cancel anytime', value: 'free', description: 'Participants can cancel without penalty' },
        { label: '24-hour cancellation', value: '24-hour', description: 'Must cancel at least 24 hours in advance' },
        { label: '7-day cancellation', value: '7-day', description: 'Must cancel at least 7 days in advance' },
        { label: '30-day cancellation', value: '30-day', description: 'Must cancel at least 30 days in advance' },
    ];

    // Update state when modal opens or currentPolicy changes
    useEffect(() => {
        if (visible) {
            setSelectedPolicy(currentPolicy || '7-day');
        }
    }, [visible, currentPolicy]);

    const handleSave = () => {
        onSave(selectedPolicy);
        onClose();
    };

    const handleCancel = () => {
        setSelectedPolicy(currentPolicy || '7-day');
        onClose();
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="fade"
            onRequestClose={onClose}
        >
            <View style={styles.overlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>{sessionTitle}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={20} color="#666" />
                        </Pressable>
                    </View>

                    {/* Content */}
                    <View style={styles.content}>
                        <View style={styles.inlineHeaderRow}>
                            <Text style={styles.label}>Cancellation Policy </Text>
                            <Text style={styles.helperInline}>
                                Choose the cancellation policy for this session
                            </Text>
                        </View>
                        
                        <View style={styles.optionsContainer}>
                            {policyOptions.map((option) => (
                                <Pressable
                                    key={option.value}
                                    style={[
                                        styles.optionItem,
                                        selectedPolicy === option.value && styles.selectedOption
                                    ]}
                                    onPress={() => setSelectedPolicy(option.value)}
                                >
                                    <View style={styles.optionContent}>
                                        <View style={styles.optionHeader}>
                                            <Text style={[
                                                styles.optionLabel,
                                                selectedPolicy === option.value && styles.selectedOptionLabel
                                            ]}>
                                                {option.label}
                                            </Text>
                                            <View style={[
                                                styles.radioButton,
                                                selectedPolicy === option.value && styles.radioButtonSelected
                                            ]}>
                                                {selectedPolicy === option.value && (
                                                    <View style={styles.radioButtonInner} />
                                                )}
                                            </View>
                                        </View>
                                        <Text style={[
                                            styles.optionDescription,
                                            selectedPolicy === option.value && styles.selectedOptionDescription
                                        ]}>
                                            {option.description}
                                        </Text>
                                    </View>
                                </Pressable>
                            ))}
                        </View>
                    </View>

                    {/* Footer */}
                    <View style={styles.footer}>
                        <Pressable onPress={handleCancel} style={[styles.button, styles.cancelButton]}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable onPress={handleSave} style={[styles.button, styles.saveButton]}>
                            <Text style={styles.saveButtonText}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        backgroundColor: 'white',
        borderRadius: 12,
        width: '100%',
        maxWidth: 500,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
    },
    label: {
        fontSize: 16,
        fontWeight: '500',
        color: '#333',
        marginBottom: 8,
    },
    inlineHeaderRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        marginBottom: 16,
    },
    helperInline: {
        fontSize: 12,
        color: '#666',
    },
    optionsContainer: {
        gap: 8,
    },
    optionItem: {
        borderWidth: 2,
        borderColor: '#e9ecef',
        borderRadius: 8,
        padding: 16,
        backgroundColor: '#f8f9fa',
    },
    selectedOption: {
        borderColor: '#007bff',
        backgroundColor: '#e3f2fd',
    },
    optionContent: {
        flex: 1,
    },
    optionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
    },
    optionLabel: {
        fontSize: 16,
        fontWeight: '500',
        color: '#333',
    },
    selectedOptionLabel: {
        color: '#007bff',
    },
    optionDescription: {
        fontSize: 12,
        color: '#666',
    },
    selectedOptionDescription: {
        color: '#0056b3',
    },
    radioButton: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 2,
        borderColor: '#ccc',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
    },
    radioButtonSelected: {
        borderColor: '#007bff',
    },
    radioButtonInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: '#007bff',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
});

export default CancellationPolicyEditModal; 